import React from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { setPricing, createSale, createOverride, signOverride } from '../api';

// Stage 5: what this class is worth, what it will be charged, and the sale.
//
// Every control from the old Pricing estimate / Pricing & sale sections is here, unchanged
// in behaviour: the forecast-is-not-the-price note, the correctable approved price with its
// author, the exception + dual-signed override flow, and the seat/price/setup sale form.
export default function PricingStage({ concept: c, conceptId, isDirector, onChanged }) {
  const [approvedPrice, setApprovedPrice] = React.useState('');
  const [editingPrice, setEditingPrice] = React.useState(false);
  const [seats, setSeats] = React.useState('');
  const [unitCents, setUnitCents] = React.useState('');
  const [setupCents, setSetupCents] = React.useState('');

  const priceMut = useMutation({
    // Send the raw string: the server validates it and rejects anything that is not a
    // positive number, instead of a parseFloat(...) || 0 that turned a typo into a free class.
    mutationFn: () => setPricing(conceptId, { director_approved_price: approvedPrice.trim() }),
    onSuccess: (res) => {
      toast.success(res?.repriced ? 'Price updated' : 'Price approved');
      setEditingPrice(false);
      if (res?.needs_executive_override) {
        toast('That price needs an executive override before this class can be sold.',
          { icon: '!', duration: 8000 });
      }
      onChanged();
    },
    onError: (e) => toast.error(e?.message || 'Could not save that price'),
  });
  const requestOverrideMut = useMutation({
    // override_type must be exactly "pricing_exception": that is what
    // TrainingConcept#pricing_exception_cleared? looks for, along with a created_at at or
    // after the exception, and two approving signatures.
    mutationFn: () => createOverride(conceptId, {
      override_type: 'pricing_exception',
      rule: c?.pricing?.exception_reason,
      exception_requested: `Approved price $${c?.pricing?.director_approved_price} (${c?.pricing?.exception_reason})`,
      authority: 'executive',
    }),
    onSuccess: () => { toast.success('Override requested - it needs a director and an executive signature'); onChanged(); },
    onError: (e) => toast.error(e?.message || 'Could not request the override'),
  });
  const ovMut = useMutation({
    mutationFn: ({ id, authority, decision }) => signOverride(id, { authority, decision }),
    onSuccess: () => { toast.success('Signature recorded'); onChanged(); },
    onError: (e) => toast.error(e?.message || 'Not permitted'),
  });
  const saleMut = useMutation({
    mutationFn: () => createSale(conceptId, {
      seats: parseInt(seats, 10) || 0,
      unit_price_cents: parseInt(unitCents, 10) || 0,
      setup_fee_cents: parseInt(setupCents, 10) || 0,
      return_url: window.location.href,
    }),
    onSuccess: (r) => {
      toast.success('Payment link created');
      if (r?.checkout_url) window.prompt('Payment link (send to client):', r.checkout_url);
      onChanged();
    },
    onError: (e) => toast.error(e?.message || 'Could not create the sale'),
  });

  // From the server (pricing_gate), which applies the same LOCKED_STATUSES rule the pricing
  // endpoint enforces. This file listed two of those six statuses, so a concept already in
  // development was told "Pricing opens once the concept clears review" - about a concept
  // that had cleared review, and whose price the server would have accepted.
  const pg = c.pricing_gate || { stage_reached: false, can_price: false, blocked_reason: null };
  const canPrice = pg.can_price;

  return (
    <div className="space-y-5">
      {c.pricing && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-[#7d8aa0] mb-2">Pricing estimate</div>
          <div className="flex justify-between text-xs text-[#c7d0e0]">
            <span>Opportunity value</span>
            <span className="font-bold text-white">
              {c.pricing.estimated_opportunity_value != null
                ? `$${Number(c.pricing.estimated_opportunity_value).toLocaleString()}` : '-'}
            </span>
          </div>
          <div className="flex justify-between text-xs text-[#c7d0e0] mt-1">
            <span>Forecast{c.pricing.buyer_stage_weight != null ? ` (weighted × ${c.pricing.buyer_stage_weight})` : ''}</span>
            <span className="font-bold text-green-400">
              {c.pricing.weighted_value != null ? `$${Number(c.pricing.weighted_value).toLocaleString()}` : '-'}
            </span>
          </div>
          <div className="text-[10px] text-[#5f6b7e] mt-2">
            A pipeline forecast (opportunity × buyer-stage likelihood), not the price. The
            price you charge is the <b>Approved price</b> below.
          </div>
        </div>
      )}

      <div className="border-t border-[#1d2634] pt-4">
        <div className="text-[10px] uppercase tracking-wide text-[#7d8aa0] mb-2">Pricing &amp; sale</div>
        {/* Priced and not being corrected: show the number, who set it, and the way to fix
            it. The editor used to vanish the moment a price saved, which left a wrong one
            (a truncated "$7" for "$770") with no route back other than a console. */}
        {c.pricing?.director_approved_price && !editingPrice ? (
          <div>
            <div className="flex items-center gap-2 text-xs text-[#c7d0e0]">
              <span>Approved price: <b className="text-white">${Number(c.pricing.director_approved_price).toLocaleString()}</b></span>
              {canPrice && (
                <button onClick={() => { setApprovedPrice(String(c.pricing.director_approved_price)); setEditingPrice(true); }}
                  className="text-[11px] text-[#5ea9ff]">Change price</button>
              )}
            </div>
            {c.pricing.approved_by && (
              <div className="text-[10px] text-[#5f6b7e] mt-1">
                Set by {c.pricing.approved_by}
                {c.pricing.approved_at ? ` on ${new Date(c.pricing.approved_at).toLocaleDateString()}` : ''}
              </div>
            )}
          </div>
        ) : (canPrice || editingPrice) ? (
          <div>
            <div className="flex gap-2 items-center">
              <input value={approvedPrice} onChange={(e) => setApprovedPrice(e.target.value)} placeholder="Approved $"
                className="w-24 bg-transparent border border-[#2b3a4d] rounded px-2 py-1 text-xs" />
              <button onClick={() => priceMut.mutate()} disabled={priceMut.isPending}
                className="text-xs px-2 py-1 rounded bg-[#1f2a38] border border-[#2b3a4d] disabled:opacity-40">
                {editingPrice ? 'Save price' : 'Approve price'}
              </button>
              {editingPrice && (
                <button onClick={() => { setEditingPrice(false); setApprovedPrice(''); }}
                  className="text-[11px] text-[#8a95a8]">Cancel</button>
              )}
            </div>
            {c.pricing?.internal_build_value != null && (
              <div className="text-[10px] text-[#5f6b7e] mt-1">
                Below the ${Number(c.pricing.internal_build_value).toLocaleString()} build cost counts as
                an exception and needs an executive override.
              </div>
            )}
            {editingPrice && c.pricing?.recognized_sale && (
              <div className="text-[10px] text-[#f7a23b] mt-1">
                This class has a recognized sale. Correcting the price does not change what
                anyone was already charged.
              </div>
            )}
          </div>
        ) : (
          // One message, the server's: either this person may not price it, or the stage is
          // not open. Two hardcoded strings here is how the wrong one came to be shown.
          <div className="text-[11px] text-[#7d8aa0]">
            {pg.blocked_reason || 'Pricing is not available for this concept yet.'}
          </div>
        )}

        {c.pricing?.pricing_exception && !c.pricing?.pricing_exception_cleared && (
          <div className="mt-3">
            <div className="text-[11px] text-[#f7a23b]">
              ⚠ Pricing exception ({c.pricing.exception_reason}) - needs executive override.
            </div>
            {isDirector && !(c.overrides || []).some((o) => o.override_type === 'pricing_exception' && o.decision === 'pending') && (
              <button onClick={() => requestOverrideMut.mutate()} disabled={requestOverrideMut.isPending}
                className="mt-1 text-[11px] px-2 py-1 rounded bg-[#1f2a38] border border-[#2b3a4d] text-[#5ea9ff] disabled:opacity-40">
                Request executive override
              </button>
            )}
            <div className="text-[10px] text-[#5f6b7e] mt-1">
              An override needs a director and an executive signature from two different
              people, and whoever requests it cannot sign it.
            </div>
          </div>
        )}
        {c.pricing?.pricing_exception && c.pricing?.pricing_exception_cleared && (
          <div className="text-[11px] text-blue-400 mt-2">
            ✓ Pricing exception ({c.pricing.exception_reason}) cleared by override.
          </div>
        )}

        {(c.overrides || []).map((o) => {
          const signed = (auth) => (o.signatures || []).find((s) => s.authority === auth);
          return (
            <div key={o.id} className="text-[11px] text-[#c7d0e0] mt-2 border-t border-[#2b3a4d] pt-2">
              <div className="flex items-center justify-between">
                <span>{o.rule || o.override_type}</span>
                <span className={o.fully_approved ? 'text-blue-400' : 'text-[#f7a23b]'}>
                  {o.fully_approved ? '✓ Dual-approved' : o.decision === 'denied' ? 'Denied' : 'Awaiting signatures'}
                </span>
              </div>
              {['director', 'executive'].map((auth) => {
                const s = signed(auth);
                return (
                  <div key={auth} className="flex items-center justify-between mt-1">
                    <span className="capitalize">{auth}: {s ? `${s.decision} (${s.user})` : 'pending'}</span>
                    {!s && o.signable?.[auth] && (
                      <span className="flex gap-2">
                        <button onClick={() => ovMut.mutate({ id: o.id, authority: auth, decision: 'approved' })}
                          className="text-blue-400">sign approve</button>
                        <button onClick={() => ovMut.mutate({ id: o.id, authority: auth, decision: 'denied' })}
                          className="text-[#f7a23b]">deny</button>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        {c.pricing?.recognized_sale ? (
          <div className="text-xs text-green-400 mt-3">✓ Sale recognized</div>
        ) : c.status === 'approved_for_sale' ? (
          <div className="mt-3">
            <div className="text-[10px] text-[#5f6b7e] mb-1">
              Seats, price per seat in cents, and any one-off setup fee.
            </div>
            <div className="flex gap-1 mb-1">
              <input value={seats} onChange={(e) => setSeats(e.target.value)} placeholder="seats"
                className="w-16 bg-transparent border border-[#2b3a4d] rounded px-2 py-1 text-xs" />
              <input value={unitCents} onChange={(e) => setUnitCents(e.target.value)} placeholder="¢/seat"
                className="w-20 bg-transparent border border-[#2b3a4d] rounded px-2 py-1 text-xs" />
              <input value={setupCents} onChange={(e) => setSetupCents(e.target.value)} placeholder="setup ¢"
                className="w-20 bg-transparent border border-[#2b3a4d] rounded px-2 py-1 text-xs" />
            </div>
            <button onClick={() => saleMut.mutate()} disabled={saleMut.isPending}
              className="text-xs px-3 py-1.5 rounded-lg bg-green-600 text-[#04120a] font-semibold disabled:opacity-40">
              {saleMut.isPending ? 'Creating…' : 'Create sale (payment link)'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
